import { EditableImage } from '../content/EditableImage';
import React from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, Clock, User, Calendar } from 'lucide-react';
import { CmsLink as Link, EditableText } from '../content/EditableText';
import { useCmsContent } from '../content/CmsContentContext';

const BlogDetail = () => {
  const { blogId } = useParams();
  const { content } = useCmsContent();
  const { blogPosts } = content;
  const post = blogPosts.find(p => p.id === parseInt(blogId));
  const postIndex = blogPosts.findIndex(p => p.id === parseInt(blogId));

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-4"><EditableText contentKey="pages.blogDetail.notFound" /></h1>
          <Link to="/blog" className="text-brand-700 hover:text-brand-800">
            <EditableText contentKey="pages.blogDetail.returnBlog" multiline={false} />
          </Link>
        </div>
      </div>
    );
  }

  const relatedPosts = blogPosts.filter(p => p.id !== post.id && p.category === post.category).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      {/* Back Button */}
      <div className="bg-gray-50 border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/blog"
            className="inline-flex items-center text-brand-700 hover:text-brand-800 font-medium"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            <EditableText contentKey="pages.blogDetail.backBlog" multiline={false} />
          </Link>
        </div>
      </div>

      {/* Hero Image */}
      <div className="relative h-96 bg-gray-900">
        <EditableImage
          contentKey={`blogPosts.${postIndex}.image`}
          alt={post.title}
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
      </div>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-6">
          <span className="bg-brand-100 text-brand-800 px-3 py-1 rounded-full font-medium">
            <EditableText contentKey={`blogPosts.${postIndex}.category`} multiline={false} />
          </span>
          <div className="flex items-center">
            <Calendar className="h-4 w-4 mr-1" />
            {new Date(post.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
          <div className="flex items-center">
            <Clock className="h-4 w-4 mr-1" />
            <EditableText contentKey={`blogPosts.${postIndex}.readTime`} multiline={false} />
          </div>
          <div className="flex items-center">
            <User className="h-4 w-4 mr-1" />
            <EditableText contentKey={`blogPosts.${postIndex}.author`} multiline={false} />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6"><EditableText contentKey={`blogPosts.${postIndex}.title`} /></h1>

        {/* Excerpt */}
        <p className="text-xl text-gray-600 mb-8 leading-relaxed"><EditableText contentKey={`blogPosts.${postIndex}.excerpt`} /></p>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-700 leading-relaxed whitespace-pre-line"><EditableText contentKey={`blogPosts.${postIndex}.content`} /></p>
        </div>

        {/* Author Info */}
        <div className="mt-12 p-6 bg-brand-50 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-2"><EditableText contentKey="pages.blogDetail.aboutAuthor" /></h3>
          <p className="text-gray-700">
            <strong><EditableText contentKey={`blogPosts.${postIndex}.author`} multiline={false} /></strong>{' '}
            <EditableText contentKey="pages.blogDetail.authorSuffix" />
          </p>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-light text-gray-900 mb-8"><EditableText contentKey="pages.blogDetail.related" /></h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => {
                const relatedIndex = blogPosts.findIndex((candidate) => candidate.id === relatedPost.id);
                return (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.id}`}
                  className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group"
                >
                  <EditableImage
                    contentKey={`blogPosts.${relatedIndex}.image`}
                    alt={relatedPost.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                      <EditableText contentKey={`blogPosts.${relatedIndex}.title`} />
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2"><EditableText contentKey={`blogPosts.${relatedIndex}.excerpt`} /></p>
                  </div>
                </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-brand-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-light mb-4"><EditableText contentKey="pages.blogDetail.ctaTitle" /></h2>
          <p className="text-xl mb-8">
            <EditableText contentKey="pages.blogDetail.ctaBody" />
          </p>
          <Link
            to="/book-appointment"
            className="inline-flex items-center px-8 py-3 bg-white text-brand-700 rounded-md hover:bg-gray-100 transition-colors text-lg font-medium"
          >
            <EditableText contentKey="pages.blogDetail.ctaButton" multiline={false} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogDetail;
